import React from "react";
import { Check } from "lucide-react";
import Card from "@/components/ui/Card.jsx";

export default function IdentityStatusCard({
  uiState,
  statusTitle,
  message,
  fields,
  panelTone,
  t,
}) {
  return (
    <Card className={`mt-4 border p-4 transition-all duration-500 ${panelTone}`}>
      <div className="flex items-center gap-4">
        <div className="relative flex items-center justify-center h-6 w-6 shrink-0">
          {uiState === "pending" ? (
            <div className="relative flex items-center justify-center h-full w-full">
              <div
                className="absolute inset-0 rounded-full animate-afrimed-beat opacity-0"
                style={{ background: "rgba(var(--afrimed-secondary), 0.35)" }}
              />
              <div
                className="absolute inset-0 rounded-full animate-afrimed-beat [animation-delay:1.5s] opacity-0"
                style={{ background: "rgba(var(--afrimed-secondary), 0.35)" }}
              />
              <div
                className="relative z-10 h-2.5 w-2.5 rounded-full shadow-[0_0_8px_rgba(var(--afrimed-secondary),0.4)]"
                style={{ background: "rgb(var(--afrimed-secondary))" }}
              />
            </div>
          ) : uiState === "verified" ? (
            <img
              src="/icons/id-passport-verified.svg"
              className="h-6 w-6 animate-in zoom-in duration-300"
              alt={t("onboarding.idPassport.alt.verified", "Verified")}
            />
          ) : uiState === "done" ? (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[rgb(var(--afrimed-tertiary))] text-white animate-in zoom-in duration-300">
              <Check size={16} strokeWidth={3} aria-hidden="true" />
            </span>
          ) : (
            <img
              src="/icons/pending-verification.svg"
              className="h-6 w-6 animate-in zoom-in duration-300"
              alt={
                uiState === "failed"
                    ? t("onboarding.idPassport.alt.failed", "Failed")
                    : uiState === "verified"
                      ? t("onboarding.idPassport.alt.verified", "Verified")
                      : t("onboarding.idPassport.alt.received", "Received")
              }
            />
          )}
        </div>

        <div className="flex-1 flex justify-between items-center">
          <span className="text-body-small text-on-surface leading-none">
            {message || statusTitle}
          </span>

          {uiState === "pending" && (
            <span className="text-[10px] uppercase tracking-widest text-[rgb(var(--afrimed-primary))] animate-pulse leading-none">
              {t("onboarding.idPassport.aiProcessing", "AI Processing")}
            </span>
          )}
        </div>
      </div>

      {fields ? (
        <div className="mt-3 text-[11px] text-on-surface-var animate-in fade-in slide-in-from-top-1">
          <div
            className="grid grid-cols-2 gap-y-4 gap-x-6 border-t pt-4"
            style={{ borderColor: "rgb(var(--clr-outline-variant))" }}
          >
            <div className="flex flex-col">
              <span className="text-[9px] uppercase opacity-50">
                {t("onboarding.idPassport.summary.surname", "Surname")}
              </span>
              <span className="text-body-small text-on-surface truncate">
                {fields.surname || "—"}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="text-[9px] uppercase opacity-50">
                {t("onboarding.idPassport.summary.idNumber", "ID No.")}
              </span>
              <span className="text-body-small text-on-surface truncate">
                {fields.document_number || "—"}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="text-[9px] uppercase opacity-50">
                {t("onboarding.idPassport.summary.dob", "Date of Birth")}
              </span>
              <span className="text-body-small text-on-surface">
                {fields.date_of_birth || "—"}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="text-[9px] uppercase opacity-50">
                {t("onboarding.idPassport.summary.expiry", "Expiry Date")}
              </span>
              <span className="text-body-small text-on-surface">
                {fields.expiry_date || "—"}
              </span>
            </div>

            <div className="flex flex-col col-span-2">
              <span className="text-[9px] uppercase opacity-50">
                {t("onboarding.idPassport.summary.givenNames", "Given Names")}
              </span>
              <span className="text-body-small text-on-surface truncate">
                {fields.given_names || "—"}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
